import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuth } from '@/hooks/common/useAuth'
import { auth } from '@/utils/firebaseConfig'
import {
  WebSocketConnectionState,
  WebSocketEvents,
  BatchProgressEventData,
  BatchStatusEventData,
  AnalysisEventData,
  ScheduledAnalysisRetryEventData,
} from '@/types/websocket'

// WebSocketはAPIパスなしでベースURLに接続する必要がある
const API_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080'
const WEBSOCKET_BASE_URL = API_URL.replace(/\/api$/, '') || 'http://localhost:8080'

interface WebSocketContextValue {
  socket: Socket | null
  connectionState: WebSocketConnectionState
  joinBatch: (batchId: string) => void
  leaveBatch: (batchId: string) => void
  onBatchProgress: (callback: (data: BatchProgressEventData) => void) => () => void
  onBatchCompleted: (callback: (data: BatchStatusEventData) => void) => () => void
  onBatchFailed: (callback: (data: BatchStatusEventData) => void) => () => void
  onAnalysisCreated: (callback: (data: AnalysisEventData) => void) => () => void
  onAnalysisCompleted: (callback: (data: AnalysisEventData) => void) => () => void
  onScheduledAnalysisRetryStarted: (
    callback: (data: ScheduledAnalysisRetryEventData) => void
  ) => () => void
  onScheduledAnalysisRetryProgress: (
    callback: (data: ScheduledAnalysisRetryEventData) => void
  ) => () => void
  onScheduledAnalysisRetryCompleted: (
    callback: (data: ScheduledAnalysisRetryEventData) => void
  ) => () => void
  onScheduledAnalysisRetryFailed: (
    callback: (data: ScheduledAnalysisRetryEventData) => void
  ) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider')
  }
  return context
}

interface WebSocketProviderProps {
  children: React.ReactNode
  enabled?: boolean // Feature flag for gradual rollout
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  enabled = true,
}) => {
  const { user } = useAuth()
  const [socket, setSocket] = useState<Socket | null>(null)
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  })
  const socketRef = useRef<Socket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
  const reconnectAttemptsRef = useRef(0)
  const hasUser = Boolean(user)
  const userIdentifier = user?.id ?? user?.email ?? null
  const primarySpaceId = user?.spaces?.[0]?.id
  const joinedSpaceRef = useRef<string | null>(null)

  const joinSpaceRoom = useCallback((spaceId: string) => {
    const activeSocket = socketRef.current
    if (!activeSocket || !activeSocket.connected) {
      return
    }
    activeSocket.emit(WebSocketEvents.JOIN_SPACE, spaceId)
    joinedSpaceRef.current = spaceId
    console.debug('[WebSocket] Joined space room:', spaceId)
  }, [])

  const leaveSpaceRoom = useCallback((spaceId?: string | null) => {
    const activeSocket = socketRef.current
    const targetSpace = spaceId ?? joinedSpaceRef.current
    if (!activeSocket || !targetSpace || !activeSocket.connected) {
      if (targetSpace && joinedSpaceRef.current === targetSpace) {
        joinedSpaceRef.current = null
      }
      return
    }

    activeSocket.emit(WebSocketEvents.LEAVE_SPACE, targetSpace)
    if (joinedSpaceRef.current === targetSpace) {
      joinedSpaceRef.current = null
    }
    console.debug('[WebSocket] Left space room:', targetSpace)
  }, [])

  // Initialize socket connection
  useEffect(() => {
    if (!enabled || !hasUser || !auth.currentUser) {
      return
    }

    const initSocket = async () => {
      try {
        setConnectionState(prev => ({ ...prev, isConnecting: true, error: null }))

        // Get Firebase auth token
        const token = await auth.currentUser!.getIdToken()

        // Create socket connection with authentication
        const newSocket = io(`${WEBSOCKET_BASE_URL}/analysis`, {
          auth: {
            token,
            spaceId: primarySpaceId, // Use first space for now
          },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5,
        })

        socketRef.current = newSocket
        setSocket(newSocket)

        // Connection event handlers
        newSocket.on('connect', () => {
          console.log('WebSocket connected')
          setConnectionState({
            isConnected: true,
            isConnecting: false,
            error: null,
          })
          reconnectAttemptsRef.current = 0

          if (primarySpaceId) {
            joinSpaceRoom(primarySpaceId)
          }
        })

        // Handle reconnection with fresh token
        newSocket.on('reconnect_attempt', async attemptNumber => {
          console.log('WebSocket reconnection attempt:', attemptNumber)
          try {
            // Refresh the auth token before reconnecting
            const freshToken = await auth.currentUser?.getIdToken(true)
            if (freshToken) {
              // Update socket auth with fresh token
              newSocket.auth = {
                token: freshToken,
                spaceId: primarySpaceId,
              }
              console.log('WebSocket auth token refreshed for reconnection')
            }
          } catch (error) {
            console.error('Failed to refresh token on reconnect:', error)
          }
        })

        newSocket.on('disconnect', reason => {
          console.log('WebSocket disconnected:', reason)
          joinedSpaceRef.current = null
          setConnectionState(prev => ({
            ...prev,
            isConnected: false,
          }))

          // If disconnected due to auth issues, ensure token refresh on next connect
          if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            // Force token refresh on next connection attempt
            newSocket.auth = async () => {
              try {
                const freshToken = await auth.currentUser?.getIdToken(true)
                return {
                  token: freshToken,
                  spaceId: primarySpaceId,
                }
              } catch (error) {
                console.error('Failed to refresh token on reconnect:', error)
                throw error
              }
            }
          }
        })

        newSocket.on('connect_error', error => {
          console.error('WebSocket connection error:', error)
          setConnectionState({
            isConnected: false,
            isConnecting: false,
            error,
          })
        })

        // Authentication events
        newSocket.on(WebSocketEvents.AUTH_SUCCESS, data => {
          console.log('WebSocket authentication successful:', data)
        })

        newSocket.on(WebSocketEvents.AUTH_FAILED, error => {
          console.error('WebSocket authentication failed:', error)
          setConnectionState({
            isConnected: false,
            isConnecting: false,
            error: new Error(error.message || 'Authentication failed'),
          })
          newSocket.disconnect()
        })

        newSocket.on(WebSocketEvents.ERROR, error => {
          console.error('WebSocket error:', error)
          setConnectionState(prev => ({
            ...prev,
            error: new Error(error.message || 'WebSocket error'),
          }))
        })
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error)
        setConnectionState({
          isConnected: false,
          isConnecting: false,
          error: error instanceof Error ? error : new Error('Failed to initialize WebSocket'),
        })
      }
    }

    initSocket()

    // Cleanup on unmount or when dependencies change
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (socketRef.current) {
        if (joinedSpaceRef.current && socketRef.current.connected) {
          socketRef.current.emit(WebSocketEvents.LEAVE_SPACE, joinedSpaceRef.current)
        }
        joinedSpaceRef.current = null
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current = null
        setSocket(null)
      }
    }
  }, [enabled, hasUser, userIdentifier, primarySpaceId, joinSpaceRoom])

  useEffect(() => {
    if (!socket || !socket.connected) {
      return
    }

    if (!primarySpaceId) {
      if (joinedSpaceRef.current) {
        leaveSpaceRoom()
      }
      return
    }

    if (joinedSpaceRef.current !== primarySpaceId) {
      if (joinedSpaceRef.current) {
        leaveSpaceRoom(joinedSpaceRef.current)
      }
      joinSpaceRoom(primarySpaceId)
    }
  }, [socket, primarySpaceId, joinSpaceRoom, leaveSpaceRoom])

  // Join batch room
  const joinBatch = useCallback(
    (batchId: string) => {
      if (!socket) {
        // Socketがまだ作成されていない場合（初期化中）
        return
      }
      if (!socket.connected) {
        // Socketが存在するが接続されていない場合
        console.debug('[WebSocket] Cannot join batch room - not connected:', {
          batchId,
          socketId: socket.id,
          connected: socket.connected,
          disconnected: socket.disconnected,
        })
        return
      }
      socket.emit(WebSocketEvents.JOIN_BATCH, batchId)
      console.debug('[WebSocket] Joined batch room:', batchId)
    },
    [socket]
  )

  // Leave batch room
  const leaveBatch = useCallback(
    (batchId: string) => {
      if (!socket || !socket.connected) {
        return
      }
      socket.emit(WebSocketEvents.LEAVE_BATCH, batchId)
      console.debug('[WebSocket] Left batch room:', batchId)
    },
    [socket]
  )

  // Event subscription helpers
  const onBatchProgress = useCallback(
    (callback: (data: BatchProgressEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.BATCH_PROGRESS, callback)
      return () => {
        socket?.off(WebSocketEvents.BATCH_PROGRESS, callback)
      }
    },
    [socket]
  )

  const onBatchCompleted = useCallback(
    (callback: (data: BatchStatusEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.BATCH_COMPLETED, callback)
      return () => {
        socket?.off(WebSocketEvents.BATCH_COMPLETED, callback)
      }
    },
    [socket]
  )

  const onBatchFailed = useCallback(
    (callback: (data: BatchStatusEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.BATCH_FAILED, callback)
      return () => {
        socket?.off(WebSocketEvents.BATCH_FAILED, callback)
      }
    },
    [socket]
  )

  const onAnalysisCreated = useCallback(
    (callback: (data: AnalysisEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.ANALYSIS_CREATED, callback)
      return () => {
        socket?.off(WebSocketEvents.ANALYSIS_CREATED, callback)
      }
    },
    [socket]
  )

  const onAnalysisCompleted = useCallback(
    (callback: (data: AnalysisEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.ANALYSIS_COMPLETED, callback)
      return () => {
        socket?.off(WebSocketEvents.ANALYSIS_COMPLETED, callback)
      }
    },
    [socket]
  )

  const onScheduledAnalysisRetryStarted = useCallback(
    (callback: (data: ScheduledAnalysisRetryEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_STARTED, callback)
      return () => {
        socket?.off(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_STARTED, callback)
      }
    },
    [socket]
  )

  const onScheduledAnalysisRetryProgress = useCallback(
    (callback: (data: ScheduledAnalysisRetryEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_PROGRESS, callback)
      return () => {
        socket?.off(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_PROGRESS, callback)
      }
    },
    [socket]
  )

  const onScheduledAnalysisRetryCompleted = useCallback(
    (callback: (data: ScheduledAnalysisRetryEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_COMPLETED, callback)
      return () => {
        socket?.off(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_COMPLETED, callback)
      }
    },
    [socket]
  )

  const onScheduledAnalysisRetryFailed = useCallback(
    (callback: (data: ScheduledAnalysisRetryEventData) => void) => {
      if (!socket) return () => {}

      socket.on(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_FAILED, callback)
      return () => {
        socket?.off(WebSocketEvents.SCHEDULED_ANALYSIS_RETRY_FAILED, callback)
      }
    },
    [socket]
  )

  const value: WebSocketContextValue = {
    socket,
    connectionState,
    joinBatch,
    leaveBatch,
    onBatchProgress,
    onBatchCompleted,
    onBatchFailed,
    onAnalysisCreated,
    onAnalysisCompleted,
    onScheduledAnalysisRetryStarted,
    onScheduledAnalysisRetryProgress,
    onScheduledAnalysisRetryCompleted,
    onScheduledAnalysisRetryFailed,
  }

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

// Optional: Export a hook to check if WebSocket is available
export const useWebSocketEnabled = () => {
  const context = useContext(WebSocketContext)
  return context !== null && context.connectionState.isConnected
}
